import { useCallback, useEffect, useState } from "react";

import { ApiError, getExtraction } from "@/api/client";
import type { Extraction } from "@/api/client";

/** How often a source still being read is asked about again. */
export const POLL_MS = 3000;

function settled(e: Extraction | null): boolean {
  return e !== null && (e.status === "done" || e.status === "failed");
}

/**
 * What was read from a past take-off and what it taught, asked again every few seconds
 * until the reading has finished. `null` while unknown; `error` when it cannot be read.
 */
export function useExtraction(documentId: string): {
  extraction: Extraction | null;
  error: string | null;
  reload: () => void;
} {
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    const ask = async () => {
      try {
        const got = await getExtraction(documentId);
        if (cancelled) return;
        setExtraction(got);
        setError(null);
        if (!settled(got)) timer = window.setTimeout(() => void ask(), POLL_MS);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.detail : "Could not reach the server.");
      }
    };
    void ask();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [documentId, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { extraction, error, reload };
}

/** One line on what a source taught, for a list. */
export function taughtLine(e: Extraction | null): string {
  if (e === null || e.status === "none" || e.status === "running") return "Reading…";
  if (e.status === "failed") return "Could not be read";
  const s = e.stats;
  const types = e.taught.length;
  if (s.text_boxes !== undefined) {
    return s.text_boxes ? `${s.text_boxes.toLocaleString()} text boxes read` : "No text found";
  }
  if (!s.boxes) return "No markup found";
  const parts = [
    `${(s.coded ?? 0).toLocaleString()} units read`,
    `${(e.codes.length ?? 0).toLocaleString()} codes`,
  ];
  if (types) parts.push(`${types} ${types === 1 ? "type" : "types"} learned`);
  return parts.join(" · ");
}
